import React, { useState, useEffect } from "react";
import axios from "axios";
import { Package, Plus, Pencil, Trash2, Search, X, Loader2, CheckCircle2, AlertCircle, ArrowDown, ArrowUp, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY = {
  nama: "",
  kecepatan: "20 Mbps",
  download_speed: 20,
  upload_speed: 20,
  harga: 0,
  mikrotik_profile: "STANDAR",
  deskripsi: "",
  status: "aktif",
};

const PaketForm = ({ initial, onClose, onSuccess }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmSync, setConfirmSync] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const affectedCount = initial?.pelanggan_total_count || 0;
  const activeCount = initial?.pelanggan_aktif_count || 0;
  const isEdit = !!initial?.id;
  const namaChanged = isEdit && String(form.nama).trim() !== String(initial?.nama ?? "").trim();
  const hargaChanged = isEdit && parseFloat(form.harga) !== parseFloat(initial?.harga ?? 0);
  const hargaDelta = (parseFloat(form.harga) || 0) - (parseFloat(initial?.harga) || 0);
  const revenueImpact = hargaDelta * activeCount;

  const doSave = async () => {
    setError("");
    setLoading(true);
    try {
      const payload = { ...form, download_speed: parseInt(form.download_speed), upload_speed: parseInt(form.upload_speed), harga: parseFloat(form.harga), kecepatan: `${form.download_speed} Mbps` };
      if (initial?.id) {
        await axios.put(`${BACKEND_URL}/api/master/paket/${initial.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/master/paket`, payload, { withCredentials: true });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal simpan paket");
      setConfirmSync(false);
    } finally { setLoading(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Jika nama/harga berubah & ada pelanggan terdampak, minta konfirmasi sinkronisasi.
    if (isEdit && (namaChanged || hargaChanged) && affectedCount > 0) {
      setConfirmSync(true);
      return;
    }
    doSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl">
        <div className="p-5 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><Package className="w-4 h-4 text-cyan-400" />{initial?.id ? "Edit Paket" : "Tambah Paket"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {confirmSync ? (
          <div className="p-5 space-y-4" data-testid="paket-sync-confirm">
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/40 text-amber-200 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="space-y-1.5">
                <p className="font-bold text-amber-100">Konfirmasi Sinkronisasi Paket</p>
                <p>Perubahan {namaChanged && "nama"}{namaChanged && hargaChanged && " & "}{hargaChanged && "harga"} paket akan disinkronkan ke <b className="text-white">{affectedCount} pelanggan</b> yang memakai paket ini.</p>
                {hargaChanged && (
                  <p className="text-[11px] text-amber-300/90">Harga: Rp {Number(initial?.harga || 0).toLocaleString("id-ID")} → <b className="text-white">Rp {Number(form.harga || 0).toLocaleString("id-ID")}</b></p>
                )}
                <p className="text-[11px] text-slate-400">Tagihan yang sudah tergenerate tidak berubah. Tagihan baru akan memakai harga terbaru.</p>
              </div>
            </div>
            {hargaChanged && hargaDelta !== 0 && (
              <div className={`p-3 rounded-xl border text-xs ${hargaDelta > 0 ? "bg-emerald-500/10 border-emerald-500/40" : "bg-rose-500/10 border-rose-500/40"}`} data-testid="paket-revenue-impact">
                <p className={`font-bold ${hargaDelta > 0 ? "text-emerald-200" : "text-rose-200"}`}>{hargaDelta > 0 ? "Estimasi Tambahan Pendapatan Bulanan" : "Estimasi Penurunan Pendapatan Bulanan"}</p>
                <p className={`text-lg font-black font-mono mt-0.5 ${hargaDelta > 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="paket-revenue-impact-value">
                  {hargaDelta > 0 ? "+" : "−"} Rp {Math.abs(revenueImpact).toLocaleString("id-ID")}<span className="text-[11px] text-slate-400 font-normal">/bulan</span>
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">{hargaDelta > 0 ? "+" : "−"}Rp {Math.abs(hargaDelta).toLocaleString("id-ID")} × {activeCount} pelanggan aktif</p>
              </div>
            )}
            {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setConfirmSync(false)} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700" data-testid="paket-sync-cancel-btn">Batal</button>
              <button type="button" onClick={doSave} disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-amber-600 to-orange-600 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="paket-sync-confirm-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Ya, Sinkronkan
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Nama Paket</label>
            <input required type="text" value={form.nama} onChange={(e) => setField("nama", e.target.value)} placeholder="Home Family 30M" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="paket-nama-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><ArrowDown className="w-3 h-3 text-cyan-400" />Download (Mbps)</label>
              <input required type="number" min={1} value={form.download_speed} onChange={(e) => setField("download_speed", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="paket-download-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><ArrowUp className="w-3 h-3 text-indigo-400" />Upload (Mbps)</label>
              <input required type="number" min={1} value={form.upload_speed} onChange={(e) => setField("upload_speed", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="paket-upload-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Harga per Bulan (Rp)</label>
            <input required type="number" min={0} value={form.harga} onChange={(e) => setField("harga", e.target.value)} placeholder="235000" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="paket-harga-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Mikrotik Profile</label>
            <input required type="text" value={form.mikrotik_profile} onChange={(e) => setField("mikrotik_profile", e.target.value)} placeholder="STANDAR / LITE / PRO" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Deskripsi</label>
            <input type="text" value={form.deskripsi} onChange={(e) => setField("deskripsi", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Status</label>
            <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200">
              <option value="aktif">Aktif</option>
              <option value="nonaktif">Nonaktif</option>
            </select>
          </div>
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700">Batal</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {initial?.id ? "Update" : "Simpan"}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export const MasterPaketPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/master/paket`, { withCredentials: true });
      setItems(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, nama) => {
    if (!window.confirm(`Hapus paket "${nama}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/master/paket/${id}`, { withCredentials: true });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Gagal hapus"); }
  };

  const totalPelanggan = items.reduce((s, p) => s + (p.pelanggan_aktif_count || 0), 0);

  return (
    <div className="space-y-4" data-testid="paket-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col md:flex-row justify-between md:items-center gap-3">
        <div>
          <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-[10px] font-bold uppercase">Master Data</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Data Paket Internet</h1>
          <p className="text-xs text-slate-400 mt-0.5">Paket layanan + jumlah pelanggan aktif per paket.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/30 flex items-center gap-2" data-testid="add-paket-btn">
          <Plus className="w-4 h-4" /> Tambah Paket
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="p-3 rounded-xl bg-[#0e1628] border border-slate-800">
          <p className="text-[10px] text-slate-400">Total Paket</p>
          <p className="text-lg font-bold text-white font-mono">{items.length}</p>
        </div>
        <div className="p-3 rounded-xl bg-[#0e1628] border border-slate-800">
          <p className="text-[10px] text-slate-400">Paket Aktif</p>
          <p className="text-lg font-bold text-emerald-400 font-mono">{items.filter(p => p.status === "aktif").length}</p>
        </div>
        <div className="p-3 rounded-xl bg-[#0e1628] border border-slate-800">
          <p className="text-[10px] text-slate-400">Pelanggan Aktif</p>
          <p className="text-lg font-bold text-cyan-400 font-mono">{totalPelanggan}</p>
        </div>
        <div className="p-3 rounded-xl bg-[#0e1628] border border-slate-800">
          <p className="text-[10px] text-slate-400">Est. MRR</p>
          <p className="text-lg font-bold text-amber-400 font-mono">Rp {items.reduce((s, p) => s + (p.harga * (p.pelanggan_aktif_count || 0)), 0).toLocaleString("id-ID")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="paket-grid">
        {loading ? <p className="text-slate-500 text-xs col-span-full text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat...</p> :
          items.length === 0 ? <p className="text-slate-500 italic text-xs col-span-full text-center py-6">Belum ada paket</p> :
            items.map((p) => (
              <div key={p.id} className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl relative" data-testid={`paket-card-${p.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-white truncate">{p.nama}</h3>
                    <span className="text-[10px] font-mono text-slate-500">Profile: {p.mikrotik_profile}</span>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${p.status === "aktif" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>{p.status?.toUpperCase()}</span>
                </div>
                <p className="text-2xl font-black text-cyan-300 font-mono mt-2">Rp {Number(p.harga).toLocaleString("id-ID")}<span className="text-xs text-slate-500 font-normal">/bulan</span></p>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-center">
                    <div className="text-[9px] text-slate-500 flex items-center justify-center gap-1"><ArrowDown className="w-2.5 h-2.5 text-cyan-400" />Download</div>
                    <p className="text-xs font-mono font-bold text-cyan-300">{p.download_speed} Mbps</p>
                  </div>
                  <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-center">
                    <div className="text-[9px] text-slate-500 flex items-center justify-center gap-1"><ArrowUp className="w-2.5 h-2.5 text-indigo-400" />Upload</div>
                    <p className="text-xs font-mono font-bold text-indigo-300">{p.upload_speed} Mbps</p>
                  </div>
                </div>
                <div className="mt-3 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[11px]">
                    <Users className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-emerald-200">Pelanggan Aktif</span>
                  </div>
                  <span className="font-mono font-bold text-emerald-300 text-sm" data-testid={`paket-pelanggan-count-${p.id}`}>{p.pelanggan_aktif_count || 0}</span>
                </div>
                {p.deskripsi && <p className="text-[10px] text-slate-500 mt-2 line-clamp-2">{p.deskripsi}</p>}
                <div className="flex gap-1 justify-end mt-3 pt-2 border-t border-slate-800">
                  <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-1.5 rounded-lg bg-slate-800 hover:bg-amber-900/60 text-amber-400"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => handleDelete(p.id, p.nama)} className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-900/60 text-rose-400"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
      </div>

      {showForm && <PaketForm initial={editing} onClose={() => setShowForm(false)} onSuccess={load} />}
    </div>
  );
};
